import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { FileUploadModule } from 'primeng/fileupload';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

// Services
import { UserService } from '../../layout/service/user.service';
import { MessageService } from 'primeng/api';

// PDF Viewer
import { PdfViewerModule } from 'ng2-pdf-viewer';

@Component({
  selector: 'app-edit-anatomi',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    FileUploadModule,
    CardModule,
    DividerModule,
    ToastModule,
    EditorModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    DialogModule,
    PdfViewerModule
  ],
  providers: [MessageService],
  template: `
    <div class="min-h-screen bg-gray-50 p-4">
      <div class="max-w-7xl mx-auto">
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <!-- Header -->
          <div class="flex justify-between items-center p-6 border-b border-gray-200">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 flex items-center">
                <i class="pi pi-pencil mr-2 text-blue-600"></i>
                Edit Anatomi
              </h2>
              <p class="text-gray-600 mt-2">Perbarui konten anatomi untuk pembelajaran OSCE</p>
            </div>
            <div class="flex gap-2">
              <p-button
                (onClick)="goBack()"
                severity="secondary"
                icon="pi pi-arrow-left"
                label="Kembali"
                [outlined]="true"
              ></p-button>
              <p-button
                (onClick)="debugData()"
                severity="help"
                icon="pi pi-info"
                label="Debug"
                [outlined]="true"
              ></p-button>
              <p-button
                (onClick)="clearAll()"
                severity="danger"
                icon="pi pi-trash"
                label="Clear Semua"
                [outlined]="true"
              ></p-button>
            </div>
          </div>

          <!-- Loading State -->
          <div *ngIf="loading" class="flex flex-col items-center justify-center py-12">
            <p-progressSpinner [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
            <p class="text-gray-600 mt-4">Memuat data anatomi...</p>
          </div>

          <!-- Form Content -->
          <div *ngIf="!loading" class="p-6">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <!-- Main Form -->
              <div class="lg:col-span-2">
                <div class="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i class="pi pi-info-circle mr-2 text-blue-600"></i>
                    Informasi Anatomi
                  </h3>

                  <!-- Nama Anatomi -->
                  <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Nama Anatomi <span class="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      [(ngModel)]="anatomyForm.nama"
                      placeholder="Masukkan nama anatomi..."
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  </div>

                  <!-- Deskripsi -->
                  <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Deskripsi <span class="text-red-500">*</span>
                    </label>
                    <textarea
                      [(ngModel)]="anatomyForm.deskripsi"
                      rows="4"
                      placeholder="Masukkan deskripsi anatomi..."
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none">
                    </textarea>
                  </div>

                  <!-- Status Aktif -->
                  <div class="mb-6">
                    <div class="flex items-center justify-between">
                      <label class="block text-sm font-medium text-gray-700">
                        Status Anatomi
                      </label>
                      <div class="flex items-center space-x-3">
                        <span class="text-sm text-gray-600">{{anatomyForm.isActive ? 'Aktif' : 'Tidak Aktif'}}</span>
                        <p-toggleswitch [(ngModel)]="anatomyForm.isActive"></p-toggleswitch>
                      </div>
                    </div>
                    <small class="text-gray-500 mt-1 block">Aktifkan untuk menampilkan anatomi ini kepada pengguna</small>
                  </div>

                  <hr class="my-6 border-gray-200">

                  <!-- Konten Anatomi -->
                  <div class="mb-6">
                    <div class="flex justify-between items-center mb-4">
                      <label class="block text-sm font-medium text-gray-700 flex items-center">
                        <i class="pi pi-list mr-2 text-blue-600"></i>
                        Konten Pembelajaran
                      </label>
                      <p-button
                        type="button"
                        (onClick)="addContent()"
                        severity="info"
                        size="small"
                        icon="pi pi-plus"
                        label="Tambah Konten"
                      ></p-button>
                    </div>

                    <div *ngFor="let content of anatomyForm.contents; let i = index"
                         id="content-{{i}}"
                         class="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50 transition-all duration-300">
                      <div class="flex justify-between items-center mb-3">
                        <span class="text-sm font-medium text-gray-600">Konten {{i + 1}}</span>
                        <p-button
                          type="button"
                          (onClick)="removeContent(i)"
                          severity="danger"
                          size="small"
                          icon="pi pi-trash"
                          [text]="true"
                        ></p-button>
                      </div>

                      <!-- Upload Gambar -->
                      <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          <i class="pi pi-image mr-1"></i>
                          Gambar Konten
                        </label>

                        <!-- Image Preview -->
                        <div *ngIf="content.imagePreview" class="mb-3">
                          <img
                            [src]="content.imagePreview"
                            alt="Preview gambar"
                            class="max-w-xs h-auto rounded border cursor-pointer"
                            (click)="viewImage(content.imagePreview)"
                          />
                          <p-button
                            icon="pi pi-times"
                            severity="danger"
                            size="small"
                            (onClick)="removeImage(i)"
                            class="ml-2"
                            [text]="true"
                          ></p-button>
                        </div>

                        <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer">
                          <input type="file" accept="image/*" (change)="onImageUpload($event, i)" class="hidden" #imageInput>
                          <div (click)="imageInput.click()">
                            <i class="pi pi-cloud-upload text-2xl text-gray-400 mb-2 block"></i>
                            <p class="text-sm text-gray-600 mb-1">
                              {{content.imageName || 'Klik untuk upload gambar'}}
                            </p>
                            <small class="text-gray-500">Max: 5MB (JPG, PNG, GIF)</small>
                          </div>
                        </div>
                      </div>

                      <!-- Editor Konten -->
                      <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          <i class="pi pi-file-edit mr-1"></i>
                          Pertanyaan
                        </label>
                        <p-editor
                          [(ngModel)]="content.question"
                          [style]="{'height':'200px'}"
                          placeholder="Tulis pertanyaan di sini...">
                        </p-editor>
                      </div>

                      <!-- Kunci Jawaban -->
                      <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          <i class="pi pi-key mr-1"></i>
                          Kunci Jawaban
                        </label>
                        <input
                          type="text"
                          [(ngModel)]="content.answerKey"
                          placeholder="Masukkan kunci jawaban..."
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      </div>

                      <!-- Upload PDF -->
                      <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          <i class="pi pi-file-pdf mr-1"></i>
                          File PDF Pendukung
                        </label>

                        <!-- PDF Preview -->
                        <div *ngIf="content.pdfPreview" class="mb-3 flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded">
                          <i class="pi pi-file-pdf text-red-500 text-xl"></i>
                          <span class="text-sm text-blue-700">{{content.pdfName || 'Dokumen PDF'}}</span>
                          <p-button
                            label="Lihat"
                            icon="pi pi-eye"
                            severity="info"
                            size="small"
                            (onClick)="viewPdf(content.pdfPreview)"
                          ></p-button>
                          <p-button
                            icon="pi pi-times"
                            severity="danger"
                            size="small"
                            (onClick)="removePdf(i)"
                            [text]="true"
                          ></p-button>
                        </div>

                        <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer">
                          <input type="file" accept=".pdf" (change)="onPdfUpload($event, i)" class="hidden" #pdfInput>
                          <div (click)="pdfInput.click()">
                            <i class="pi pi-file-pdf text-2xl text-red-500 mb-2 block"></i>
                            <p class="text-sm text-gray-600 mb-1">
                              {{content.pdfName || 'Klik untuk upload PDF'}}
                            </p>
                            <small class="text-gray-500">Max: 10MB (PDF)</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div *ngIf="anatomyForm.contents.length === 0"
                         class="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <i class="pi pi-inbox text-4xl text-gray-400 mb-3 block"></i>
                      <p class="text-gray-600 mb-1">Belum ada konten pembelajaran</p>
                      <small class="text-gray-500">Klik "Tambah Konten" untuk menambah materi</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sidebar -->
              <div class="lg:col-span-1">
                <div class="bg-white border border-gray-200 rounded-lg p-6 h-fit">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i class="pi pi-info-circle mr-2 text-blue-600"></i>
                    Informasi Tambahan
                  </h3>

                  <!-- Preview Status -->
                  <div class="mb-6 p-4 bg-blue-50 rounded-lg">
                    <div class="flex items-center justify-between">
                      <span class="text-sm font-medium text-blue-800">Status:</span>
                      <span class="px-2 py-1 text-xs rounded-full {{anatomyForm.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}}">
                        {{anatomyForm.isActive ? 'Aktif' : 'Tidak Aktif'}}
                      </span>
                    </div>
                    <div class="mt-2">
                      <span class="text-sm text-blue-600">Total Konten: {{anatomyForm.contents.length}}</span>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="space-y-4">
                    <!-- Primary Action -->
                    <div class="border-b border-gray-200 pb-4">
                      <p-button
                        label="Simpan Perubahan"
                        icon="pi pi-check"
                        severity="success"
                        class="w-full"
                        size="large"
                        (onClick)="onSubmit()"
                        [loading]="saving"
                        [disabled]="saving"
                      ></p-button>
                    </div>

                    <!-- Secondary Actions -->
                    <div class="space-y-2">
                      <p-button
                        label="Reload Data"
                        icon="pi pi-refresh"
                        severity="info"
                        class="w-full"
                        (onClick)="reloadData()"
                        [loading]="loading"
                        [outlined]="true"
                      ></p-button>

                      <p-button
                        label="Batal"
                        icon="pi pi-times"
                        severity="secondary"
                        class="w-full"
                        (onClick)="goBack()"
                        [outlined]="true"
                      ></p-button>
                    </div>
                  </div>
                </div>

                <!-- Daftar Pertanyaan Card -->
                <div class="bg-white border border-gray-200 rounded-lg p-6 h-fit mt-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i class="pi pi-list-check mr-2 text-blue-600"></i>
                    Daftar Pertanyaan
                  </h3>

                  <div *ngIf="anatomyForm.contents.length === 0" class="text-center py-8">
                    <i class="pi pi-inbox text-3xl text-gray-400 mb-2 block"></i>
                    <p class="text-sm text-gray-500">Belum ada pertanyaan</p>
                    <small class="text-gray-400">Tambahkan konten untuk melihat daftar pertanyaan</small>
                  </div>

                  <div *ngIf="anatomyForm.contents.length > 0" class="space-y-3">
                    <div *ngFor="let content of anatomyForm.contents; let i = index"
                         class="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                         (click)="scrollToContent(i)">
                      <div class="flex items-start space-x-3">
                        <div class="flex-shrink-0">
                          <span class="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                            {{i + 1}}
                          </span>
                        </div>
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-gray-800 mb-1">
                            Pertanyaan {{i + 1}}
                          </p>
                          <p class="text-xs text-gray-600 line-clamp-2">
                            {{getContentPreview(content)}}
                          </p>
                          <div class="mt-2 flex items-center space-x-2">
                            <span class="inline-flex items-center text-xs text-gray-500">
                              <i class="pi pi-image mr-1" [class.text-green-500]="content.imageName" [class.text-gray-400]="!content.imageName"></i>
                              {{content.imageName ? 'Ada gambar' : 'Tanpa gambar'}}
                            </span>
                            <span class="inline-flex items-center text-xs text-gray-500">
                              <i class="pi pi-file-pdf mr-1" [class.text-green-500]="content.pdfName" [class.text-gray-400]="!content.pdfName"></i>
                              {{content.pdfName ? 'Ada PDF' : 'Tanpa PDF'}}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast Messages -->
      <p-toast></p-toast>

      <!-- Image Preview Dialog -->
      <p-dialog
        header="Preview Gambar"
        [(visible)]="showImageDialog"
        [modal]="true"
        [style]="{ width: '80vw', maxWidth: '800px' }"
        [closable]="true"
      >
        <div *ngIf="selectedImage" class="text-center">
          <img
            [src]="selectedImage"
            alt="Preview gambar"
            class="max-w-full h-auto rounded shadow-lg"
          />
        </div>
      </p-dialog>

      <!-- PDF Viewer Dialog -->
      <p-dialog
        header="Viewer PDF"
        [(visible)]="showPdfDialog"
        [modal]="true"
        [style]="{ width: '90vw', height: '85vh' }"
        [maximizable]="true"
        [closable]="true"
        (onHide)="closePdfDialog()"
      >
        <div *ngIf="selectedPdf" class="h-full">
          <pdf-viewer
            [src]="selectedPdf"
            [render-text]="true"
            [original-size]="false"
            [fit-to-page]="true"
            [show-all]="true"
            [page]="1"
            [zoom]="1.0"
            [rotation]="0"
            [show-borders]="true"
            [loading]="'Memuat PDF...'"
            [error]="'Error memuat PDF'"
            style="display: block; width: 100%; height: 75vh; border: 1px solid #ccc;"
            (after-load-complete)="onPdfLoadComplete($event)"
            (error)="onPdfError($event)"
          ></pdf-viewer>
        </div>
      </p-dialog>
    </div>
  `
})
export class EditAnatomi implements OnInit {
  anatomyId!: number;
  loading: boolean = false;
  saving: boolean = false;
  showImageDialog: boolean = false;
  showPdfDialog: boolean = false;
  selectedImage: string | null = null;
  selectedPdf: any = null;

  anatomyForm: any = {
    id: '',
    nama: '',
    deskripsi: '',
    isActive: true,
    contents: []
  };

  constructor(
    private userService: UserService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    // Get anatomy ID from route params
    this.route.params.subscribe(params => {
      this.anatomyId = +params['id'];
      if (this.anatomyId) {
        this.loadAnatomyData();
      } else {
        console.error('No anatomy ID found in route params');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'ID anatomi tidak ditemukan'
        });
        this.goBack();
      }
    });
  }  async loadAnatomyData() {
    this.loading = true;

    try {
      // First, try to get anatomy basic info from list
      let anatomyBasicInfo = null;
      try {
        const listResponse = await this.userService.getAnatomy({
          page: 1,
          limit: 100
        });


        if (listResponse && listResponse.status && listResponse.data) {
          const anatomyList = listResponse.data;
          anatomyBasicInfo = anatomyList.find((item: any) =>
            (item.id == this.anatomyId) || (item.anatomy_id == this.anatomyId)
          );
        }
      } catch (error) {
        console.log('Could not load anatomy list:', error);
      }

      // Load anatomy details
      const response = await this.userService.getAnatomyDetails({
        anatomy_id: this.anatomyId
      });


      if (response && response.status) {
        const anatomyData = response.data;

        // Handle different response structures
        let anatomyInfo = anatomyBasicInfo || {}; // Use basic info as fallback
        let contentDetails = [];

        // Check if response.data is an array (details) or object with anatomy info
        if (Array.isArray(anatomyData)) {
          // If data is array, it's just the details/contents
          contentDetails = anatomyData;
          // Try to get anatomy info from first detail or use basic info
          if (anatomyData.length > 0 && anatomyData[0].anatomy_name) {
            anatomyInfo = {
              name: anatomyData[0].anatomy_name,
              description: anatomyData[0].anatomy_description || '',
              is_active: anatomyData[0].anatomy_is_active !== undefined ? anatomyData[0].anatomy_is_active : true
            };
          }
        } else if (anatomyData && typeof anatomyData === 'object') {
          // If data is object, check for details property
          if (anatomyData.details) {
            contentDetails = anatomyData.details;
            // Use the object itself for anatomy info if available
            if (anatomyData.name || anatomyData.anatomy_name) {
              anatomyInfo = anatomyData;
            }
          } else if (anatomyData.contents) {
            contentDetails = anatomyData.contents;
            anatomyInfo = anatomyData;
          } else {
            // Maybe the object itself contains the content properties
            contentDetails = [anatomyData];
          }
        }

        // Populate basic form data with priority: anatomyInfo > anatomyBasicInfo > defaults
        this.anatomyForm.id = this.anatomyId;
        this.anatomyForm.nama = anatomyInfo.name || anatomyInfo.anatomy_name ||
                                anatomyBasicInfo?.name || anatomyBasicInfo?.anatomy_name ||
                                `Anatomi ${this.anatomyId}`;
        this.anatomyForm.deskripsi = anatomyInfo.description || anatomyInfo.anatomy_description ||
                                     anatomyBasicInfo?.description || anatomyBasicInfo?.anatomy_description || '';
        this.anatomyForm.isActive = anatomyInfo.is_active !== undefined ? anatomyInfo.is_active :
                                    (anatomyInfo.anatomy_is_active !== undefined ? anatomyInfo.anatomy_is_active :
                                    (anatomyBasicInfo?.is_active !== undefined ? anatomyBasicInfo.is_active :
                                    (anatomyBasicInfo?.anatomy_is_active !== undefined ? anatomyBasicInfo.anatomy_is_active : true)));

        // Populate content data
        this.anatomyForm.contents = [];
        if (contentDetails && contentDetails.length > 0) {
          contentDetails.forEach((content: any) => {
            const contentItem = {
              id: content.id || content.detail_id || '',
              question: content.question || content.content || content.pertanyaan || '',
              explanation: content.explanation || content.penjelasan || '',
              answerKey: content.key_answer || content.answer_key || content.kunci_jawaban || '',
              image: content.image || content.gambar || '',
              pdf: content.pdf || content.file_pdf || '',
              imageName: (content.image || content.gambar) ? 'Gambar tersedia' : '',
              pdfName: (content.pdf || content.file_pdf) ? 'PDF tersedia' : '',
              imagePreview: this.getImageSrc(content.image || content.gambar),
              pdfPreview: content.pdf || content.file_pdf || null
            };
            this.anatomyForm.contents.push(contentItem);
          });
        }

        // Show success message with loaded data info
        this.messageService.add({
          severity: 'success',
          summary: 'Berhasil',
          detail: `Data anatomi "${this.anatomyForm.nama}" berhasil dimuat`
        });

      } else {
        throw new Error(response?.message || 'Gagal memuat data anatomi');
      }
    } catch (error) {
      console.error('Error loading anatomy data:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal memuat data anatomi'
      });
      this.goBack();
    } finally {
      this.loading = false;
    }
  }

  addContent() {
    const newContent = {
      id: '',
      question: '',
      explanation: '',
      answerKey: '',
      image: '',
      pdf: '',
      imageName: '',
      pdfName: '',
      imagePreview: null,
      pdfPreview: null
    };
    this.anatomyForm.contents.push(newContent);

    // Scroll to new content
    setTimeout(() => {
      this.scrollToContent(this.anatomyForm.contents.length - 1);
    }, 100);
  }

  removeContent(index: number) {
    this.anatomyForm.contents.splice(index, 1);
  }

  onImageUpload(event: any, contentIndex: number) {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ukuran file terlalu besar. Maksimal 5MB.'
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = e.target.result.split(',')[1]; // Remove data URL prefix
        const content = this.anatomyForm.contents[contentIndex];
        content.image = base64;
        content.imageName = file.name;
        content.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);

      // Clear the input
      event.target.value = '';
    }
  }

  onPdfUpload(event: any, contentIndex: number) {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ukuran file terlalu besar. Maksimal 10MB.'
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = e.target.result.split(',')[1]; // Remove data URL prefix
        const content = this.anatomyForm.contents[contentIndex];
        content.pdf = base64;
        content.pdfName = file.name;
        content.pdfPreview = base64;
      };
      reader.readAsDataURL(file);

      // Clear the input
      event.target.value = '';
    }
  }

  removeImage(contentIndex: number) {
    const content = this.anatomyForm.contents[contentIndex];
    content.image = '';
    content.imageName = '';
    content.imagePreview = null;
  }

  removePdf(contentIndex: number) {
    const content = this.anatomyForm.contents[contentIndex];
    content.pdf = '';
    content.pdfName = '';
    content.pdfPreview = null;
  }

  getImageSrc(base64Image: string): string | null {
    if (!base64Image) return null;

    // Check if it's already a data URL
    if (base64Image.startsWith('data:')) {
      return base64Image;
    }

    // Check if it's a regular URL
    if (base64Image.startsWith('http')) {
      return base64Image;
    }

    // Check if it's PDF data (should not be displayed as image)
    if (this.isPdfData(base64Image)) {
      return null;
    }

    // Assume it's base64 string, add data URL prefix
    return `data:image/jpeg;base64,${base64Image}`;
  }

  viewImage(imageSrc: string) {
    this.selectedImage = imageSrc;
    this.showImageDialog = true;
  }

  viewPdf(pdfData: string) {
    try {
      if (pdfData.startsWith('data:')) {
        this.selectedPdf = pdfData;
      } else {
        this.selectedPdf = `data:application/pdf;base64,${pdfData}`;
      }
      this.showPdfDialog = true;
    } catch (error) {
      console.error('Error viewing PDF:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal menampilkan PDF'
      });
    }
  }

  closePdfDialog() {
    this.selectedPdf = null;
    this.showPdfDialog = false;
  }

  onPdfLoadComplete(event: any) {
    console.log('PDF loaded successfully:', event);
  }

  onPdfError(error: any) {
    console.error('PDF load error:', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Error PDF',
      detail: 'Gagal memuat file PDF. File mungkin rusak atau format tidak didukung.'
    });
  }

  isPdfData(base64String: string): boolean {
    if (!base64String) return false;

    try {
      const binaryString = atob(base64String);
      return binaryString.startsWith('%PDF');
    } catch (error) {
      return false;
    }
  }

  scrollToContent(index: number) {
    const element = document.getElementById(`content-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  getContentPreview(content: any): string {
    if (content.question) {
      // Strip HTML tags and get first 50 characters
      const plainText = content.question.replace(/<[^>]*>/g, '');
      return plainText.length > 50 ? plainText.substring(0, 50) + '...' : plainText;
    }
    return 'Konten kosong';
  }

  clearAll() {
    this.anatomyForm = {
      id: '',
      nama: '',
      deskripsi: '',
      isActive: true,
      contents: []
    };
  }

  debugData() {
    this.messageService.add({
      severity: 'info',
      summary: 'Debug Info',
      detail: `ID: ${this.anatomyId}, Nama: ${this.anatomyForm.nama}, Contents: ${this.anatomyForm.contents.length}`
    });
  }

  async reloadData() {
    await this.loadAnatomyData();
  }

  async onSubmit() {
    // Validate form
    if (!this.anatomyForm.nama || !this.anatomyForm.deskripsi) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Peringatan',
        detail: 'Mohon lengkapi nama dan deskripsi anatomi'
      });
      return;
    }

    if (this.anatomyForm.contents.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Peringatan',
        detail: 'Mohon tambahkan minimal satu konten pembelajaran'
      });
      return;
    }

    // Validate content
    for (let i = 0; i < this.anatomyForm.contents.length; i++) {
      const content = this.anatomyForm.contents[i];
      if (!content.question || !content.question.trim()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Peringatan',
          detail: `Pertanyaan pada konten ${i + 1} wajib diisi`
        });
        return;
      }
    }

    this.saving = true;
    try {
      // Prepare data for API - matching the required JSON structure
      const updateData = {
        id: this.anatomyForm.id,
        name: this.anatomyForm.nama,
        description: this.anatomyForm.deskripsi,
        draft: "0", // Always set to "0" for active content
        is_active: this.anatomyForm.isActive ? 1 : 0,
        details: this.anatomyForm.contents.map((content: any) => {
          const detail: any = {
            question: content.question,
            key_answer: content.answerKey,
            image: content.image || "",
            explanation: content.pdf || "" // PDF data goes to explanation field
          };

          // Only include ID if it exists (for updating existing details)
          if (content.id) {
            detail.id = content.id;
          }

          return detail;
        })
      };


      // Call updateAnatomy API
      const response = await this.userService.updateAnatomy(updateData);

      if (response && response.status) {
        this.messageService.add({
          severity: 'success',
          summary: 'Berhasil',
          detail: 'Data anatomi berhasil diperbarui'
        });

        // Navigate back to list after short delay
        setTimeout(() => {
          this.goBack();
        }, 1500);
      } else {
        throw new Error(response?.message || 'Gagal memperbarui anatomi');
      }
    } catch (error) {
      console.error('Error updating anatomy:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal memperbarui data anatomi. Silakan coba lagi.'
      });
    } finally {
      this.saving = false;
    }
  }

  goBack() {
    this.router.navigate(['/app/osce/anatomi/list']);
  }
}